import { useState } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeaderProps {
  title: string;
  subtitle?: string;
  codebaseName?: string;
  connected?: boolean;
  isDocker?: boolean;
}

function smartPath(fullPath: string): string {
  const segments = fullPath.split('/').filter(Boolean);
  if (segments.length <= 3) return fullPath;
  return '…/' + segments.slice(-3).join('/');
}

export function Header({
  title,
  subtitle,
  codebaseName,
  connected,
  isDocker,
}: HeaderProps): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const openInVSCode = (): void => {
    if (subtitle) {
      // Normalize backslashes to forward slashes for the vscode:// URI
      const normalizedPath = subtitle.replace(/\\/g, '/');
      window.open(`vscode://file/${normalizedPath}`, '_blank');
    }
  };

  const copyPath = (): void => {
    if (subtitle) {
      void navigator.clipboard.writeText(subtitle).then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1500);
      });
    }
  };

  return (
    <header className="relative flex h-14 shrink-0 items-center border-b border-border px-6">
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <h1 className="text-sm font-semibold text-text-primary tracking-tight">{title}</h1>
        {subtitle ? (
          <div className="mt-0.5 flex min-w-0 items-center gap-2 text-[11px] text-text-secondary">
            {codebaseName && <span className="truncate">{codebaseName}</span>}
            {codebaseName && <span className="text-text-tertiary">·</span>}
            <button
              onClick={copyPath}
              className="group flex min-w-0 items-center gap-1 truncate text-left font-mono text-text-tertiary transition-colors hover:text-text-primary"
              title={subtitle}
            >
              <span className="truncate">{smartPath(subtitle)}</span>
              {copied ? (
                <Check className="size-3 shrink-0 text-success" />
              ) : (
                <Copy className="size-3 shrink-0 opacity-0 group-hover:opacity-100" />
              )}
            </button>
          </div>
        ) : codebaseName ? (
          <span className="mt-0.5 text-[11px] text-text-secondary">{codebaseName}</span>
        ) : connected !== undefined ? (
          <span className="mt-0.5 text-[11px] italic text-text-tertiary">No codebase</span>
        ) : null}
      </div>
      <div className="ml-auto flex items-center gap-3">
        {subtitle && !isDocker && (
          <button
            onClick={openInVSCode}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-surface hover:text-text-primary"
            title="Open in VS Code"
          >
            <ExternalLink className="size-3.5" />
            <span>Open in IDE</span>
          </button>
        )}
        {connected !== undefined && (
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                'size-1.5 rounded-full',
                connected ? 'bg-success shadow-[0_0_8px_var(--success)]' : 'bg-text-tertiary'
              )}
            />
            <span className="font-mono text-[10.5px] uppercase tracking-widest text-text-tertiary">
              {connected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        )}
      </div>
    </header>
  );
}
